/**
 * Web Push klientská logika (reliability refactor).
 * Viz docs/plans/PUSH-NOTIFICATIONS-RELIABILITY.md.
 *
 *  - registerServiceWorker(): registrace SW na úrovni celé app + controllerchange
 *    reload guard (reload max jednou, přeskočí initial claim → žádný reload loop).
 *  - ensureSubscribed(): refresh-on-load self-heal — porovná applicationServerKey
 *    s aktuálním VAPID klíčem, při mismatchi unsubscribe+resubscribe, pak UPSERT
 *    na server. Vyžaduje permission 'granted'.
 *  - requestAndSubscribe(): user-gesture flow (request permission → subscribe).
 *  - unsubscribe(): odhlášení tohoto zařízení (lokálně + server).
 */

import { postWebPushSubscribe, postWebPushUnsubscribe } from "@client/api/endpoints";

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC as string | undefined;

const IDB_NAME = "demirel-push";
const IDB_STORE = "kv";
const VAPID_KEY = "vapidPublicKey";

export const isPushSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  "serviceWorker" in navigator &&
  typeof window !== "undefined" &&
  "PushManager" in window &&
  "Notification" in window &&
  // Service Worker / Push běží jen v secure contextu (HTTPS nebo localhost). Na HTTP LAN/VPN
  // je tím celý push subsystém no-op (UI dostane "unsupported") bez chyb v konzoli.
  window.isSecureContext;

export const currentPermission = (): NotificationPermission | undefined =>
  typeof Notification !== "undefined" ? Notification.permission : undefined;

/** Detekce iOS standalone PWA (Web Push na iOS funguje jen po "Add to Home Screen"). */
export const isStandalonePwa = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
};

export const isIos = (): boolean =>
  typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

const urlBase64ToUint8Array = (base64String: string): Uint8Array<ArrayBuffer> => {
  const clean = base64String.trim(); // ochrana proti trailing newline z env
  const padding = "=".repeat((4 - (clean.length % 4)) % 4);
  const base64 = (clean + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
};

/** Uloží VAPID public key do IndexedDB pro SW pushsubscriptionchange handler. */
const idbSetVapidKey = (value: string): Promise<void> =>
  new Promise((resolve) => {
    try {
      const open = indexedDB.open(IDB_NAME, 1);
      open.onupgradeneeded = () => open.result.createObjectStore(IDB_STORE);
      open.onerror = () => resolve();
      open.onsuccess = () => {
        const db = open.result;
        const tx = db.transaction(IDB_STORE, "readwrite");
        tx.objectStore(IDB_STORE).put(value, VAPID_KEY);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => resolve();
      };
    } catch {
      resolve();
    }
  });

const keysEqual = (a: ArrayBuffer | null | undefined, b: Uint8Array): boolean => {
  if (!a) return false;
  const av = new Uint8Array(a);
  if (av.length !== b.length) return false;
  for (let i = 0; i < av.length; i++) if (av[i] !== b[i]) return false;
  return true;
};

/** subscribe() s retry — AbortError z push služby (FCM) bývá i transientní. */
const subscribeWithRetry = async (
  reg: ServiceWorkerRegistration,
  appServerKey: Uint8Array<ArrayBuffer>
): Promise<PushSubscription> => {
  let lastErr: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey });
    } catch (err) {
      lastErr = err;
      if (err instanceof DOMException && err.name === "AbortError" && attempt < 2) {
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
};

let registration: ServiceWorkerRegistration | null = null;
let swInitStarted = false;

/** Registrace SW + controllerchange reload guard. Idempotentní (StrictMode-safe). */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isPushSupported()) return null;
  if (swInitStarted) return registration;
  swInitStarted = true;

  let refreshing = false;
  // Přeskoč první controllerchange způsobený initial clients.claim() (žádný předchozí
  // controller) — reloaduj jen při skutečném update SW. Brání reload loopu.
  let firstControllerSettled = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    if (!firstControllerSettled) {
      firstControllerSettled = true;
      return;
    }
    refreshing = true;
    window.location.reload();
  });

  try {
    registration = await navigator.serviceWorker.register("/service-worker.js");
    return registration;
  } catch (err) {
    console.error("Service Worker registrace selhala:", err);
    return null;
  }
};

const getRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (registration) return registration;
  if (!isPushSupported()) return null;
  registration = await navigator.serviceWorker.ready.catch(() => null);
  return registration;
};

/** Vrací aktuální subscription (nebo null). */
export const getSubscription = async (): Promise<PushSubscription | null> => {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

/**
 * Refresh-on-load self-heal. Vyžaduje permission 'granted'. Porovná
 * applicationServerKey existující subscription s aktuálním VAPID klíčem; při
 * mismatchi (rotace klíče) unsubscribe + resubscribe. Vždy UPSERT na server.
 */
export const ensureSubscribed = async (): Promise<PushSubscription | null> => {
  if (!isPushSupported() || !VAPID_PUBLIC) return null;
  if (currentPermission() !== "granted") return null;
  const reg = await getRegistration();
  if (!reg) return null;

  const appServerKey = urlBase64ToUint8Array(VAPID_PUBLIC);
  let sub = await reg.pushManager.getSubscription();

  if (sub) {
    const existingKey = sub.options?.applicationServerKey as ArrayBuffer | null;
    if (!keysEqual(existingKey, appServerKey)) {
      await sub.unsubscribe().catch(() => undefined);
      sub = null;
    }
  }
  if (!sub) {
    sub = await subscribeWithRetry(reg, appServerKey);
  }

  await idbSetVapidKey(VAPID_PUBLIC); // pro SW pushsubscriptionchange
  await postWebPushSubscribe(sub.toJSON() as unknown as PushSubscription); // UPSERT (self-heal)
  return sub;
};

export type EnableResult =
  | { ok: true }
  | {
      ok: false;
      reason: "unsupported" | "no-vapid" | "denied" | "default" | "ios-not-installed" | "push-service-blocked" | "bad-key" | "error";
    };

/** User-gesture flow: request permission → subscribe → upsert. */
export const requestAndSubscribe = async (): Promise<EnableResult> => {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  if (!VAPID_PUBLIC) return { ok: false, reason: "no-vapid" };
  // iOS: Web Push jde jen v nainstalované PWA (standalone).
  if (isIos() && !isStandalonePwa()) return { ok: false, reason: "ios-not-installed" };
  try {
    const permission = await Notification.requestPermission();
    if (permission === "denied") return { ok: false, reason: "denied" };
    if (permission !== "granted") return { ok: false, reason: "default" };
    const sub = await ensureSubscribed();
    return sub ? { ok: true } : { ok: false, reason: "error" };
  } catch (err) {
    console.error("requestAndSubscribe failed:", err);
    if (err instanceof DOMException) {
      // AbortError = push služba selhala (typicky Brave bez "Use Google services for push messaging");
      // "missing applicationServerKey" v message = config chyba.
      if (err.name === "AbortError") {
        return { ok: false, reason: /applicationServerKey/i.test(err.message) ? "bad-key" : "push-service-blocked" };
      }
      if (err.name === "NotAllowedError") return { ok: false, reason: "denied" };
      if (err.name === "InvalidAccessError") return { ok: false, reason: "bad-key" };
    }
    return { ok: false, reason: "error" };
  }
};

/** Odhlásí toto zařízení (lokálně + na serveru). */
export const unsubscribeThisDevice = async (): Promise<void> => {
  const reg = await getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await postWebPushUnsubscribe(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
};
